import _ from 'lodash';

import momentConfig from './common/time/time.config';
import {configLanguage} from './common/language.utils';
import {configureKeepaliveInterceptor} from './common/keepalive/keepalive.service';

import angularSpinner from '../common/wrappers/angular-spinner';
import ngStorage from '../common/wrappers/ngstorage';
import APP_CONFIG from './app.config';
import {whitelistAuthURLs} from './auth_method/auth_method.component';

/**
 * Common configuration that needs to be run for all bundles.
 */

/**
 * Set debug message visibility.
 */
function configLogProvider($logProvider) {
  'ngInject';
  let debug = false;
  if ('debug' in APP_CONFIG && APP_CONFIG.debug) {
    debug = true;
  }

  console.log('Setting debug level', debug); // eslint-disable-line no-console
  $logProvider.debugEnabled(debug);
}

configLogProvider.imports = [];

/**
 * Configure whitelist where resources are allowed to be loaded from.
 */
function configResourceWhitelist($sceDelegateProvider) {
  'ngInject';

  $sceDelegateProvider.resourceUrlWhitelist(
  $sceDelegateProvider.resourceUrlWhitelist().concat([
    'self',
    'https://www.oral.fi',
    'https://maps.google.com'
  ]));
}

/**
 * Configure global spinner.
 *
 * Refer to http://fgnass.github.io/spin.js/
 */
function configureSpinner(usSpinnerConfigProvider) {
  'ngInject';

  // Change default color to Oral Blue
  usSpinnerConfigProvider.setDefaults({
    color: '#009AAA'
  });
}

configureSpinner.imports = [angularSpinner];

/**
 * Configure local and session storage prefix to avoid clashes with other apps in the same domain.
 */
function configureLocalStorage($localStorageProvider, $sessionStorageProvider) {
  'ngInject';

  $localStorageProvider.setKeyPrefix('OmaOral-');
  $sessionStorageProvider.setKeyPrefix('OmaOral-');
}

configureLocalStorage.imports = [ngStorage];

/**
 * Optimize debug-disabled app by removing unused debug information.
 * See: https://ng-perf.com/2014/10/24/simple-trick-to-speed-up-your-angularjs-app-load-time/
 */
function optimizeApp($compileProvider) {
  'ngInject';

  let debugInfo = false;
  if ('debug' in APP_CONFIG) {
    debugInfo = APP_CONFIG.debug;
  }

  $compileProvider.debugInfoEnabled(debugInfo);
}
optimizeApp.imports = [];

/**
 * Remove $location entirely from AngularJS. Since our dependencies might use $location and using it inserts hash change
 * listener code we do not want, we need to remove the possibility of importing $location entirely. This is done by
 * changing the $locationProvider.$get function to return an empty mock $location.
 *
 * absUrl() and url() are supported with window.location for Angulartics to work.
 */
function initialiseFakeLocationService($locationProvider) {
  'ngInject';
  $locationProvider.$get = () => {
    return new class{
      absUrl() {
        return window.location.href;
      }

      url() {
        return window.location.hash.substr(1);
      }

      protocol() {}
      host() {}
      port() {}
      path() {}
      search() {}
      hash() {}
      replace() {}
      state() {}
    };
  };
}
initialiseFakeLocationService.imports = [];

/**
 * Disable Angular default anchorscroll functionality where it scrolls when location
 * hash changes. There is no need for the this and it scrolls when for example
 * locations list has fully loaded and loading all images takes longer than it takes
 * to load the page which results in page jumping to top after user has already interacted with the page.
 * @param $anchorScrollProvider
 */
function disableAutoScroll($anchorScrollProvider) {
  'ngInject';
  $anchorScrollProvider.disableAutoScrolling();
}

disableAutoScroll.imports = [];

/**
 * Execute all default configs in this file.
 * @param mod An angular module object.
 * @returns The angular module object.
 */
function defaultConfigs(mod) {
  return mod
  .config(configLogProvider)
  .config(optimizeApp)
  .config(configResourceWhitelist)
  .config(configureLocalStorage)
  .config(configLanguage)
  .config(configureSpinner)
  .config(momentConfig)
  .config(configureKeepaliveInterceptor)
  .config(initialiseFakeLocationService)
  .config(whitelistAuthURLs)
  .config(disableAutoScroll)
  ;
}

defaultConfigs.imports = _.union(
  configLogProvider.imports,
  optimizeApp.imports,
  configResourceWhitelist.imports,
  configureLocalStorage.imports,
  configLanguage.imports,
  configureSpinner.imports,
  momentConfig.imports,
  configureKeepaliveInterceptor.imports,
  initialiseFakeLocationService.imports,
  whitelistAuthURLs.imports,
  disableAutoScroll.imports,
);

export {
  configLogProvider,
  configResourceWhitelist,
  configLanguage,
  configureSpinner,
  configureLocalStorage,
  optimizeApp,
  momentConfig,
  configureKeepaliveInterceptor,
  initialiseFakeLocationService,
  disableAutoScroll,
};

export default defaultConfigs;



// WEBPACK FOOTER //
// ./src/app/common.config.js